import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Grid,
  Box,
} from '@mui/material';
import axios from 'axios';
import { groupDataByTimeRange } from '../utils/chartUtils';
import API_BASE from '../apiBase';
import {
  BusinessPerformance,
  EngineeringHealth,
  SniffrBenefitsCard,
  IssueDetectionTrends,
  DashboardFilters
} from '../components/dashboard';
import { DashboardSkeleton } from '../components/dashboard/SkeletonLoader';
import {
  NoDataEmptyState,
  NoProjectsEmptyState,
  ErrorEmptyState
} from '../components/dashboard/EmptyState';
import ErrorBoundary from '../components/dashboard/ErrorBoundary';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPRsAnalyzed: 0,
    totalJirasAnalyzed: 0,
    issuesFound: { critical: 0, medium: 0, low: 0 },
    avgReviewTime: '0h',
  });

  const [subscription, setSubscription] = useState({
    prsUsed: 0,
    prsLimit: 0,
    planName: 'Unknown',
    status: 'Unknown',
  });

  const [issueTrends, setIssueTrends] = useState({
    critical: [],
    medium: [],
    low: []
  });

  const [jiraAccuracy, setJiraAccuracy] = useState(0);
  const [workTypeDistribution, setWorkTypeDistribution] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [authors, setAuthors] = useState([]);
  const [repos, setRepos] = useState([]);
  const [recentPRs, setRecentPRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to force refresh

  const [filters, setFilters] = useState({
    projectKey: '',
    dateRange: '30d',
    author: 'all',
    repository: 'all',
  });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        setError(null);
        const { data } = await axios.get(`${API_BASE}/api/projects-with-subscriptions`);

        // Transform data to match expected format
        const transformedProjects = data.map((item) => {
          const project = item.project || item;
          return {
            ...project,
            api_key: project.api_key,
          };
        });

        setProjects(transformedProjects);

        if (transformedProjects.length > 0) {
          const firstProject = transformedProjects[0];
          const projectKey = firstProject.api_key;
          // Update both states together to avoid triggering multiple effects
          setSelectedProject(projectKey);
          setFilters((prev) => {
            // Only update if different to avoid unnecessary re-renders
            if (prev.projectKey !== projectKey) {
              return { ...prev, projectKey };
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again.');
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
  }, []); // Only run once on mount

  useEffect(() => {
    // Don't fetch if projects are still loading or no project is selected
    if (projectsLoading || !selectedProject) {
      if (!selectedProject) {
        setLoading(false);
      }
      return;
    }

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get(`${API_BASE}/api/dashboard`, {
          params: {
            ...filters,
            project_key: selectedProject,
            range: filters.dateRange,
          },
        });

        const defaultStats = {
          totalPRsAnalyzed: 0,
          totalJirasAnalyzed: 0,
          issuesFound: { critical: 0, medium: 0, low: 0 },
          avgReviewTime: '0h',
        };

        setStats({ ...defaultStats, ...data.stats });
        setSubscription(data.subscription || {
          prsUsed: 0,
          prsLimit: 0,
          planName: 'Unknown',
          status: 'Unknown',
          daysUntilExpiry: 0
        });
        setIssueTrends(data.issueTrends || { critical: [], medium: [], low: [] });
        setJiraAccuracy(data.jiraAccuracy || data.trelloAccuracy || data.azureBoardsAccuracy || 0);
        // Use Trello work type distribution if available, otherwise Azure Boards, otherwise JIRA
        setWorkTypeDistribution(data.trelloWorkTypeDistribution || data.azureBoardsWorkTypeDistribution || data.workTypeDistribution || {});
        setAuthors(['all', ...(data.authors || [])]);
        setRepos(['all', ...(data.repositories || [])]);
        setRecentPRs(data.recentPRs || []);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedProject, filters, projectsLoading, refreshTrigger]); // Include refreshTrigger to allow manual refresh

  const handleFilterChange = useCallback((name, value) => {
    if (name === 'projectKey') {
      setSelectedProject(value);
    }
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleRetry = useCallback(() => {
    setError(null);
    if (projects.length === 0) {
      // Retry fetching projects
      window.location.reload();
    } else {
      // Trigger refresh by incrementing refreshTrigger
      // This will cause the useEffect to re-run and fetch dashboard data
      setRefreshTrigger((prev) => prev + 1);
    }
  }, [projects.length]);

  const hasData = useMemo(() => (
    stats.totalPRsAnalyzed > 0
    || stats.totalJirasAnalyzed > 0
    || stats.totalAzureBoardsAnalyzed > 0
    || stats.totalTrelloAnalyzed > 0
    || (issueTrends.critical?.length > 0
      || issueTrends.medium?.length > 0
      || issueTrends.low?.length > 0)
  ), [stats, issueTrends]);

  // const formattedIssueData = useMemo(() => {
  //   const criticalData = groupDataByTimeRange(
  //     issueTrends.critical || [],
  //     filters.dateRange,
  //     'date',
  //     'count'
  //   );
  //   const mediumData = groupDataByTimeRange(
  //     issueTrends.medium || [],
  //     filters.dateRange,
  //     'date',
  //     'count'
  //   );
  //   const lowData = groupDataByTimeRange(
  //     issueTrends.low || [],
  //     filters.dateRange,
  //     'date',
  //     'count'
  //   );

  //   const allKeys = [
  //     ...new Set([
  //       ...criticalData.map((d) => d.label),
  //       ...mediumData.map((d) => d.label),
  //       ...lowData.map((d) => d.label)
  //     ])
  //   ];

  //   return allKeys.map((label) => ({
  //     label,
  //     critical: criticalData.find((d) => d.label === label)?.total_value || 0,
  //     medium: mediumData.find((d) => d.label === label)?.total_value || 0,
  //     low: lowData.find((d) => d.label === label)?.total_value || 0,
  //   }));
  // }, [issueTrends, filters.dateRange]);

  // const formattedJiraData = useMemo(() =>
  //   groupDataByTimeRange(jiraAccuracy || [], filters.dateRange, 'date', 'accuracy'),
  //   [jiraAccuracy, filters.dateRange]
  // );

  // const totalIssuesBySeverity = useMemo(() => {
  //   const criticalCount = (issueTrends.critical || [])
  //     .reduce((sum, item) => sum + item.count, 0);
  //   const mediumCount = (issueTrends.medium || [])
  //     .reduce((sum, item) => sum + item.count, 0);
  //   const lowCount = (issueTrends.low || [])
  //     .reduce((sum, item) => sum + item.count, 0);

  //   return { critical: criticalCount, medium: mediumCount, low: lowCount };
  // }, [issueTrends]);

  // const totalIssues = useMemo(() => {
  //   if (!issueTrends?.critical || !issueTrends?.medium || !issueTrends?.low) {
  //     return 0;
  //   }

  //   const criticalCount = (issueTrends.critical || [])
  //     .reduce((sum, item) => sum + item.count, 0);
  //   const mediumCount = (issueTrends.medium || [])
  //     .reduce((sum, item) => sum + item.count, 0);
  //   const lowCount = (issueTrends.low || [])
  //     .reduce((sum, item) => sum + item.count, 0);

  //   return criticalCount + mediumCount + lowCount;
  // }, [issueTrends]);

  // const averageJiraAccuracy = useMemo(() => {
  //   if (!jiraAccuracy?.length) {
  //     return 0;
  //   }

  //   const total = jiraAccuracy.reduce((sum, item) => sum + item.accuracy, 0);
  //   return total / jiraAccuracy.length;
  // }, [jiraAccuracy]);

  // Show loading state (only when there are projects or projects are still loading)
  if (projectsLoading || (loading && projects.length > 0)) {
    return <DashboardSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ bgcolor: "#fff", minHeight: "100vh", color: "#1f1f1f", p: { xs: 1, sm: 2, md: 6 } }}>
        <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
          <ErrorEmptyState
            title="Dashboard Error"
            description={error}
            onAction={handleRetry}
            actionText="Retry"
          />
        </Container>
      </Box>
    );
  }

  // Show no projects state
  if (projects.length === 0) {
    return (
      <Box sx={{ bgcolor: "#fff", minHeight: "100vh", color: "#1f1f1f", p: { xs: 1, sm: 2, md: 6 } }}>
        <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
          <NoProjectsEmptyState
            onAction={() => window.location.href = '/projects'}
            actionText="Go to Projects"
          />
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#f3f4f6',
        minHeight: '100vh',
        color: '#171717',
        p: { xs: 2, sm: 3, md: 4 }
      }}
      role="main"
      aria-label="Executive Dashboard"
    >
      <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        {/* Dashboard Filters */}
        <Box
          sx={{
            borderRadius: 1,
            p: '1rem',
            border: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            mb: { xs: 3, md: 4 },
            display: 'block'
          }}
        >
          <DashboardFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            projects={projects}
            authors={authors}
            repositories={repos}
            showProjectFilter
            showDateRangeFilter
            showAuthorFilter
          />
        </Box>

        {/* Show no data state if no data available */}
        {!hasData ? (
          <NoDataEmptyState
            onAction={handleRetry}
            actionText="Refresh Data"
          />
        ) : (
          <>
            {/* Engineering Health Section */}
            <ErrorBoundary>
              <EngineeringHealth
                stats={stats}
                subscription={subscription}
                issueTrends={issueTrends}
                averageJiraAccuracy={jiraAccuracy}
                projects={projects}
                selectedProject={selectedProject}
              />
            </ErrorBoundary>

            {/* Business Performance Section */}
            <ErrorBoundary>
              <BusinessPerformance
                stats={stats}
                subscription={subscription}
                issueTrends={issueTrends}
                workTypeDistribution={workTypeDistribution}
                projects={projects}
                selectedProject={selectedProject}
              />
            </ErrorBoundary>

            {/* Issue Detection Trends and Executive Health Summary Section */}
            <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mt: 1 }}>
              <Grid item xs={12} lg={12}>
                <ErrorBoundary>
                  <IssueDetectionTrends
                    issueTrends={issueTrends}
                    filters={filters}
                  />
                </ErrorBoundary>
              </Grid>
              {/* <Grid item xs={12} lg={4}>
                <ErrorBoundary>
                  <SniffrBenefitsCard
                    stats={stats}
                    subscription={subscription}
                    issueTrends={issueTrends}
                  />
                </ErrorBoundary>
              </Grid> */}
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
}

export default Dashboard; 